import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Heading,
  Text,
  VStack,
  Avatar,
  Spinner,
  Button,
  Input,
  HStack,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ChatState } from "../Context/ChatProvider";
import { StarRating } from "../components/StarRating";

const CategoryPage = () => {
  const { category } = useParams();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { user, setChats, chats, setSelectedChat } = ChatState();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const formattedCategory = category;
        // Public endpoint - no auth required
        const response = await axios.get(
          `/api/user/category?category=${formattedCategory}`
        );

        setUsers(response.data);
        setFilteredUsers(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users: ", error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, [category]);

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = users.filter((user) =>
      user.name.toLowerCase().includes(value)
    );
    setFilteredUsers(filtered);
  };

  const handleChatNow = async (vendorId) => {
    // Check if user is logged in
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const { data } = await axios.post(
        "/api/chat",
        { userId: vendorId },
        config
      );

      if (!chats.find((chat) => chat._id === data._id)) {
        setChats([data, ...chats]);
      }

      setSelectedChat(data);
      navigate(`/chats`);
      setLoading(false);
    } catch (error) {
      console.error("Error accessing chat: ", error);
      setLoading(false);
    }
  };

  return (
    <Box p={8} textAlign="center">
      <Button
        leftIcon={<ArrowBackIcon />}
        colorScheme="blue"
        variant="outline"
        onClick={() => navigate("/")}
        position="absolute"
        left={8}
      >
        Back
      </Button>

      <Heading fontSize="4xl" color="orange.700" mt={12} mb={4}>
        {category} Services
      </Heading>

      <Input
        placeholder={`Search ${category} vendors...`}
        value={searchTerm}
        onChange={handleSearchChange}
        maxW="600px"
        margin="0 auto"
        mb={10}
        borderColor="orange.500"
        focusBorderColor="orange.600"
      />

      {loading ? (
        <Spinner size="xl" color="orange.500" />
      ) : (
        <VStack spacing={4}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <Box
                key={user._id}
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                p={4}
                w="100%"
                maxW="600px"
                boxShadow="lg"
                textAlign="center"
              >
                <Avatar size="xl" name={user.name} src={user.pic} mb={4} />
                <Heading fontSize="2xl">{user.name}</Heading>
                <Text fontSize="lg" color="gray.500">
                  {user.email}
                </Text>

                {/* Display vendor rating */}
                <Box mt={2} display="flex" justifyContent="center">
                  <StarRating
                    rating={user.averageRating || 0}
                    totalRatings={user.totalRatings || 0}
                    size={18}
                  />
                </Box>

                <Text mt={2} color="orange.600" fontWeight="semibold">
                  {user.categories && user.categories.length > 0
                    ? user.categories.join(", ")
                    : "No categories listed"}
                </Text>

                {/* Updated buttons layout */}
                <HStack spacing={4} justify="center" mt={4}>
                  <Button
                    colorScheme="orange"
                    onClick={() => handleChatNow(user._id)}
                  >
                    Chat Now
                  </Button>
                  <Button
                    colorScheme="teal"
                    onClick={() =>
                      navigate(`/vendor/${user._id}`, { state: { category } })
                    }
                  >
                    View Profile
                  </Button>
                </HStack>
              </Box>
            ))
          ) : (
            <Text>No users found for this category.</Text>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default CategoryPage;
